/**
 * Unified error handling utilities for both web and mobile platforms
 * Provides a consistent way to display errors regardless of platform
 */

import { Platform } from 'react-native';
import { showAlert } from './alert';

/**
 * Error display function type
 * This allows us to inject platform-specific error handlers
 */
export type ErrorDisplayFunction = (message: string, title?: string) => void;

/**
 * Default error handler for mobile (uses alerts)
 */
const defaultMobileErrorHandler: ErrorDisplayFunction = (message: string, title?: string) => {
  showAlert(title || 'Error', message);
};

/**
 * Global error handler that can be overridden per context
 * Web contexts (like toast context) can override this
 */
let globalErrorHandler: ErrorDisplayFunction = defaultMobileErrorHandler;

/**
 * Set a custom global error handler
 * Used by web contexts to inject toast notifications
 */
export const setGlobalErrorHandler = (handler: ErrorDisplayFunction) => {
  globalErrorHandler = handler;
};

/**
 * Reset error handler to default (mobile alerts)
 */
export const resetGlobalErrorHandler = () => {
  globalErrorHandler = defaultMobileErrorHandler;
};

/**
 * Show error using the appropriate method for the current platform
 * @param message - Error message to display
 * @param title - Optional error title (defaults to 'Error')
 */
export const showError = (message: string, title?: string) => {
  globalErrorHandler(message, title);
};

/**
 * Extract a user-friendly error message from various error types
 * 
 * API error response format:
 * {
 *   success: false,
 *   error: {
 *     code: 3,
 *     description: "This Apple account does not have permission to use the Resident Portal",
 *     message: "Unable to login with the provided username and password",
 *     status: 403
 *   }
 * }
 * 
 * RTK Query error format (after unwrap):
 * {
 *   status: 403,
 *   data: { ...API response above }
 * }
 */
export const extractErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  // Handle different error formats
  if (typeof error === 'string') {
    return error;
  }

  // RTK Query error format: error.data.error.description (most specific)
  if (error?.data?.error?.description) {
    return error.data.error.description;
  }

  // RTK Query error format: error.data.description
  if (error?.data?.description) {
    return error.data.description;
  }

  // Direct error object with description (from API response)
  if (error?.error?.description) {
    return error.error.description;
  }

  // Direct description field
  if (error?.description) {
    return error.description;
  }

  // RTK Query error format: error.data.error.message
  if (error?.data?.error?.message) {
    return error.data.error.message;
  }

  // RTK Query error format: error.data.message
  if (error?.data?.message) {
    return error.data.message;
  }

  // Standard error message
  if (error?.message) {
    return error.message;
  }

  // Axios-style response: error.response.data.description
  if (error?.response?.data?.description) {
    return error.response.data.description;
  }

  // Axios-style response: error.response.data.message
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Nested error object message
  if (error?.error?.message) {
    return error.error.message;
  }

  return defaultMessage;
};

/**
 * Handle Google login errors with appropriate messaging
 */
export const handleGoogleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Google login failed');
  const title = 'Sign-in Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

/**
 * Handle Apple login errors with appropriate messaging
 */
export const handleAppleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Apple login failed');
  const title = 'Sign-in Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

/**
 * Handle standard login errors
 */
export const handleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Login failed');
  const title = 'Login Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

